<template>
    <v-container fluid>
        <v-row class="d-flex justify-space-between">
            <v-col cols="auto">
                <h2 class="text-h5 font-weight-bold pb-5">
                    <img
                        v-if="entries.length"
                        :src="
                            entries[0].form_type === 'truck'
                                ? truckPng
                                : warePng
                        "
                        :class="[
                            entries[0].form_type === 'truck'
                                ? 'heading-icon-truck'
                                : 'heading-icon-ware',
                            'mr-2',
                        ]"
                    />
                    {{
                        entries.length
                            ? entries[0].form_type === "truck"
                                ? `${$t("entries.truckEntries")}`
                                : `${$t("entries.wareEntries")}`
                            : $t("entries.default")
                    }}
                </h2>
            </v-col>
            <v-col cols="auto">
                <v-btn
                    v-if="$route.path === '/dashboard/my-trucks'"
                    @click="$router.push('/dashboard/publish-truck')"
                    >+ {{ $t("sidebar.publishTruck") }}
                </v-btn>
                <v-btn
                    v-if="$route.path === '/dashboard/my-wares'"
                    @click="$router.push('/dashboard/publish-ware')"
                    >+ {{ $t("sidebar.publishWare") }}
                </v-btn>
            </v-col>
        </v-row>
        <div v-auto-animate>
            <EntriesFilterBar
                v-model="filters"
                :form-type="listFormType"
                :sort-direction="sortDirection"
                :results-count="filteredAndSortedEntries.length"
                @toggle-sort="toggleSort"
            />

            <v-row
                v-if="entries.length"
                class="entries-list"
                :data-cy="$route.name === 'MyTrucks' ? 'my-trucks-list' : 'my-wares-list'"
            >
                <v-col
                    v-for="entry in paginatedEntries"
                    :key="entry.uid"
                    cols="12"
                    md="12"
                >
                <v-tooltip
                    :disabled="!isEntryGreyedOut(entry)"
                    open-delay="500"
                    location="top"
                >
                    <template #activator="{ props }">
                        <div v-bind="props" class="entry-card-inner">
                            <v-expand-transition>
                                <!-- SMALL CARD -->
                                <v-card
                                    v-if="entry.uid !== expandedEntryUid"
                                    :class="{ 'entry-greyed-out': isEntryGreyedOut(entry) }"
                                    :key="`small-${entry.uid}`"
                                    class="d-flex flex-column"
                                    :data-cy="`entry-card-${entry.uid}`"
                                    @click="handleEntryClick(entry)"
                                >
                        <v-card-text class="font-weight-bold text-body-1 pa-0">
                            <div class="d-flex align-stretch w-100 entry-small-card__layout">
                                <div class="flex-grow-1 pa-4 min-w-0">
                                    <!-- LOADING LOCATION -->
                                    ID: {{ entry.identification_id }}
                                    <img
                                        :src="loadingPng"
                                        class="entry-icon-small"
                                        alt="Loading"
                                    />
                                    {{
                                        entry.loading_location?.countryName?.code
                                    }}, {{ entry.loading_location?.cityName }},
                                    {{ entry.loading_location.postal_code }}
                                    |
                                    {{
                                        formatDateRange(
                                            entry.loading_location.date_start,
                                            entry.loading_location.date_end
                                        )
                                    }}
                                    <span
                                        v-if="entry.loading_location.time_start"
                                        >,
                                        {{
                                            entry.loading_location.time_start?.slice(
                                                0,
                                                5
                                            )
                                        }}
                                        -
                                        {{
                                            entry.loading_location.time_end?.slice(
                                                0,
                                                5
                                            )
                                        }} </span
                                    >
                                    <v-tooltip
                                        v-if="extraStopCount(entry, 'loading') > 0"
                                        :text="extraStopTooltip(entry, 'loading')"
                                        location="top"
                                    >
                                        <template #activator="{ props }">
                                            <v-chip
                                                v-bind="props"
                                                size="x-small"
                                                variant="tonal"
                                                color="primary"
                                                class="mx-1"
                                            >+{{ extraStopCount(entry, 'loading') }}</v-chip>
                                        </template>
                                    </v-tooltip>
                                    &nbsp;

                                    <img
                                        :src="unloadingPng"
                                        class="entry-icon-small"
                                        alt="Unloading"
                                    />
                                    {{
                                        entry.unloading_location?.countryName
                                            ?.code
                                    }}, {{ entry.unloading_location?.cityName }},
                                    {{ entry.unloading_location.postal_code }} |
                                    {{
                                        formatDateRange(
                                            entry.unloading_location.date_start,
                                            entry.unloading_location.date_end
                                        )
                                    }}
                                    <span
                                        v-if="
                                            entry.unloading_location.time_start
                                        "
                                    >,
                                        {{
                                            entry.unloading_location.time_start?.slice(
                                                0,
                                                5
                                            )
                                        }}
                                        -
                                        {{
                                            entry.unloading_location.time_end?.slice(
                                                0,
                                                5
                                            )
                                        }}
                                    </span>
                                    <v-tooltip
                                        v-if="extraStopCount(entry, 'unloading') > 0"
                                        :text="extraStopTooltip(entry, 'unloading')"
                                        location="top"
                                    >
                                        <template #activator="{ props }">
                                            <v-chip
                                                v-bind="props"
                                                size="x-small"
                                                variant="tonal"
                                                color="primary"
                                                class="mx-1"
                                            >+{{ extraStopCount(entry, 'unloading') }}</v-chip>
                                        </template>
                                    </v-tooltip>
                                    |
                                    {{
                                        entry.transport_options
                                            ?.truck_transport_type || "N/A"
                                    }},
                                    {{
                                        entry.transport_options
                                            ?.truck_coating || "N/A"
                                    }},

                                    <span v-if="entry.form_type === 'ware'">
                                        {{
                                            entry.transport_options
                                                ?.platform_type || "N/A"
                                        }}
                                    </span>
                                    <span
                                        v-if="
                                            entry.transport_options
                                                ?.dangerous_load
                                        "
                                    >
                                        {{ $t("formFields.dangerousLoad") }}
                                    </span>
                                    <span
                                        v-if="
                                            entry.form_type === 'ware' &&
                                            entry.transport_options
                                                .escrow_account === true
                                        "
                                        >, Escrow</span
                                    >
                                    <span v-if="entry.form_type === 'truck'">
                                        {{
                                            entry.transport_options
                                                ?.truck_type || "N/A"
                                        }}
                                    </span>
                                    <span
                                        v-if="
                                            formatSpecialFeatures(
                                                entry.transport_options
                                            )
                                        "
                                        >,
                                        {{
                                            formatSpecialFeatures(
                                                entry.transport_options
                                            )
                                        }}
                                    </span>
                                    | {{ entry.info.weight }}t/
                                    <span v-if="entry.info.volume">
                                        {{ entry.info.volume }}m³
                                    </span>
                                    <span v-if="entry.info.mdp">/&nbsp;{{ $t("entries.floorAreaUnit") }}: {{ entry.info.mdp }}m.</span> |
                                    <span v-if="entry.info.price"
                                        >{{ entry.info.price }}
                                        {{ entry.info.currency }}
                                        <span
                                            v-if="entry.info.vat_type === true"
                                            >{{
                                                $t("formFields.withVAT")
                                            }}</span
                                        >
                                        <span v-else
                                            >{{
                                                $t("formFields.withoutVAT")
                                            }}
                                            |</span
                                        ></span
                                    > 

                                    {{ entry.info.payment_term }} |

                                    <!-- DISTANCE  -->
                                    <template v-if="entry.distance_km">
                                        <strong>{{
                                            $t("entries.distance")
                                        }}</strong>:
                                        {{ entry.distance_km }} km
                                    </template>
                                    <span v-if="entry.notes">
                                        |
                                        <span
                                            style="
                                                font-weight: normal !important;
                                            "
                                        >
                                            {{ entry.notes }}
                                        </span>
                                    </span>
                                </div>

                                <aside
                                    v-if="getSmallCardEstimateRows(entry).length"
                                    class="entry-small-card__estimates d-flex flex-column justify-center align-start flex-shrink-0 align-self-stretch bg-primary pa-3 px-3 text-no-wrap"
                                >
                                    <div
                                        v-for="row in getSmallCardEstimateRows(entry)"
                                        :key="row.key"
                                        class="entry-small-card__estimate-row d-flex align-center"
                                    >
                                        <v-icon
                                            :icon="row.icon"
                                            size="18"
                                            color="yellow"
                                            class="flex-shrink-0"
                                        />
                                        <span
                                            class="text-body-2 text-yellow font-weight-bold ml-2"
                                        >
                                            {{ row.value }}
                                        </span>
                                        <v-tooltip
                                            activator="parent"
                                            location="left"
                                        >
                                            {{ row.tooltip }}
                                        </v-tooltip>
                                    </div>
                                </aside>
                            </div>
                        </v-card-text>
                    </v-card>
                                <!-- BIG CARD -->
                                <v-card
                                    v-else
                                    :key="`big-${entry.uid}`"
                                    :class="['d-flex flex-column big-card', { 'entry-greyed-out': isEntryGreyedOut(entry) }]"
                                    @click="handleEntryClick(entry)"
                                >
                        <!-- Toolbar -->
                        <v-toolbar
                            color="primary"
                            density="compact"
                            class="entry-card-toolbar"
                            :class="{
                                'entry-card-toolbar--sm': $vuetify.display.smAndDown,
                                'pr-4': !$vuetify.display.smAndDown,
                            }"
                        >
                            <template v-if="$vuetify.display.smAndDown">
                                <v-row no-gutters align="center" class="w-100 px-2 py-1">
                                    <v-col cols="12" class="text-body-2 font-weight-bold">
                                        <div class="entry-toolbar-meta entry-toolbar-meta--compact d-flex flex-wrap align-center justify-center ga-3">
                                            <template v-if="entry.identification_id">
                                                <span
                                                    class="toolbar-left-group cursor-pointer"
                                                    @click.stop="copyEntryId(entry.identification_id)"
                                                    @mousedown.stop
                                                    @touchstart.stop
                                                >
                                                    <span class="text-white">ID:&nbsp;</span>
                                                    <span class="text-yellow">{{ entry.identification_id }}</span>
                                                    <v-btn
                                                        icon
                                                        size="x-small"
                                                        density="compact"
                                                        variant="text"
                                                        color="yellow"
                                                        class="ml-1 mb-1"
                                                    >
                                                        <v-icon size="16">mdi-content-copy</v-icon>
                                                    </v-btn>
                                                    <v-tooltip activator="parent" location="bottom">
                                                        {{ $t("entries.copyId") }}
                                                    </v-tooltip>
                                                </span>
                                            </template>

                                            <template v-if="entry.distance_km != null">
                                                <span
                                                    class="toolbar-left-group distance-toggle cursor-pointer"
                                                    @click.stop="toggleRouteMap(entry.uid)"
                                                    @mousedown.stop
                                                    @touchstart.stop
                                                >
                                                    <span class="text-white">{{ $t("entries.distance") }}:&nbsp;</span>
                                                    <span class="text-yellow">{{ entry.distance_km }} km</span>
                                                    <v-tooltip activator="parent" location="bottom">
                                                        {{
                                                            isRouteMapExpanded(entry.uid)
                                                                ? $t("entries.closeMap")
                                                                : $t("entries.openMap")
                                                        }}
                                                    </v-tooltip>
                                                </span>
                                            </template>

                                            <template v-if="entry.company">
                                                <span
                                                    class="toolbar-left-group cursor-pointer"
                                                    @click.stop="goToCompanyIncidents(entry.company.id)"
                                                    @mousedown.stop
                                                    @touchstart.stop
                                                >
                                                    <span class="text-white">{{ $t("entries.incidents") }}:&nbsp;</span>
                                                    <span class="text-yellow">{{ getTotalIncidents(entry) }}</span>
                                                    <v-tooltip activator="parent" location="bottom">
                                                        <div>
                                                            {{ $t("entries.paymentIncidents") }}:
                                                            {{ getPaymentIncidentsCount(entry) }}
                                                        </div>
                                                        <div>
                                                            {{ $t("entries.transportIncidents") }}:
                                                            {{ getTransportIncidentsCount(entry) }}
                                                        </div>
                                                    </v-tooltip>
                                                </span>
                                            </template>
                                        </div>
                                    </v-col>

                                    <v-col
                                        cols="12"
                                        class="entry-toolbar-calculators-row d-flex justify-center pt-2"
                                    >
                                        <EntryCalculatorsMenu
                                            :entry="entry"
                                            :show-duration="canShowTripDurationMenu(entry)"
                                            :duration-chip-label="getDurationChipLabel(entry)"
                                            :active-panel="getActiveCalculatorPanel(entry.uid)"
                                            :length-cm="getFloorAreaLength(entry.uid)"
                                            :width-cm="getFloorAreaWidth(entry.uid)"
                                            :package-count="getFloorAreaPackageCount(entry.uid)"
                                            :applied-floor-area-label="appliedFloorAreaByUid[entry.uid] ?? ''"
                                            :speed-kmh="getTripDurationSpeed(entry)"
                                            :price-per-liter="getFuelPrice(entry.uid)"
                                            :distance-km="getFuelDistance(entry)"
                                            :consumption-l-per100-km="getFuelConsumption(entry.uid)"
                                            :applied-fuel-cost-label="appliedFuelCostByUid[entry.uid] ?? ''"
                                            @update:active-panel="setActiveCalculatorPanel(entry.uid, $event)"
                                            @update:length-cm="setFloorAreaLength(entry.uid, $event)"
                                            @update:width-cm="setFloorAreaWidth(entry.uid, $event)"
                                            @update:package-count="setFloorAreaPackageCount(entry.uid, $event)"
                                            @update:speed-kmh="setCalculatorSpeed(entry.uid, $event)"
                                            @update:price-per-liter="setFuelPrice(entry.uid, $event)"
                                            @update:distance-km="setFuelDistance(entry.uid, $event)"
                                            @update:consumption-l-per100-km="setFuelConsumption(entry.uid, $event)"
                                            @apply-floor="applyFloorArea(entry, $event)"
                                            @apply-duration="applyTripDuration(entry, $event)"
                                            @apply-fuel="applyFuelCost(entry, $event)"
                                        />
                                    </v-col>

                                    <v-col
                                        v-if="canShowReserveButton(entry)"
                                        cols="12"
                                        class="entry-toolbar-reserve-row d-flex justify-center pt-2"
                                    >
                                        <v-btn
                                            size="large"
                                            color="white"
                                            variant="flat"
                                            class="text-primary entry-toolbar-reserve-btn"
                                            @click.stop="openReservationModal(entry)"
                                        >
                                            {{ $t("entries.reserve") }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </template>

                            <template v-if="$vuetify.display.smAndDown" #extension>
                                <div class="w-100">
                                    <v-row no-gutters align="center" class="px-2 pb-1">
                                        <v-col cols="12" class="d-flex justify-center align-center flex-wrap ga-1" @click.stop>
                                            <div v-if="entry.company" class="d-flex align-center">
                                                <v-tooltip location="bottom">
                                                    <template #activator="{ props: tooltipProps }">
                                                        <span v-bind="tooltipProps" class="d-inline-flex align-center">
                                                            <star-rating-display
                                                                :model-value="entry.company.rating_average ?? 0"
                                                                :show-value="false"
                                                                :star-size="18"
                                                                star-color="#ffeb3b"
                                                                inactive-color="#616161"
                                                            />
                                                        </span>
                                                    </template>
                                                    <template v-if="entry.company.rating_average != null">
                                                        {{ $t("entries.companyRating") }}
                                                        {{ entry.company.rating_average }}
                                                    </template>
                                                    <template v-else>
                                                        {{ $t("entries.noCompanyRatingsYet") }}
                                                    </template>
                                                </v-tooltip>
                                            </div>
                                            <span class="toolbar-item-separator"></span>
                                            <div>
                                                <span class="text-yellow text-body-2 font-weight-bold">{{ entry.view_count ?? 0 }}</span>
                                                <v-menu
                                                    :open-on-hover="false"
                                                    :close-on-content-click="false"
                                                    :scrim="false"
                                                    location="bottom"
                                                    @update:model-value="(open) => open && fetchViewers(entry)"
                                                >
                                                    <template #activator="{ props: menuProps }">
                                                        <v-btn
                                                            icon
                                                            size="large"
                                                            elevation="0"
                                                            color="yellow"
                                                            density="compact"
                                                            class="cursor-default"
                                                            v-bind="menuProps"
                                                        >
                                                            <v-icon>mdi-eye-outline</v-icon>
                                                        </v-btn>
                                                        <span class="toolbar-item-separator"></span>
                                                    </template>
                                                    <div class="viewers-menu-list">
                                                        <template v-if="(viewersCache[entry.uid] || []).length">
                                                            <div
                                                                v-for="(viewer, index) in viewersCache[entry.uid]"
                                                                :key="index"
                                                                class="viewer-item text-body-2 py-1 px-4"
                                                                @click.stop="goToCompanyDetails(viewer.company_id)"
                                                            >
                                                                {{ [viewer.first_name, viewer.last_name].filter(Boolean).join(" ") || "-" }}{{ viewer.company ? `, ${viewer .company}` : "" }}
                                                            </div>
                                                        </template>
                                                        <div v-else class="viewer-item py-1 px-4">
                                                            {{ $t("entries.noViewers") }}
                                                        </div>
                                                    </div>
                                                </v-menu>
                                            </div>
                                            <div>
                                                <v-btn
                                                    icon
                                                    size="large"
                                                    elevation="0"
                                                    color="yellow"
                                                    density="compact"
                                                    class="cursor-default"
                                                    @click.stop
                                                >
                                                    <v-icon>mdi-information-outline</v-icon>
                                                    <v-tooltip activator="parent" location="bottom">
                                                        <div>
                                                            {{ $t("entries.createdAt") }}:
                                                            {{ formatLocalTime(entry.created_at) }}
                                                        </div>
                                                        <div>
                                                            {{ $t("entries.modifiedAt") }}:
                                                            {{ formatLocalTime(entry.modified_at) }}
                                                        </div>
                                                    </v-tooltip>
                                                </v-btn>
                                            </div>
                                            <div>
                                                <v-btn
                                                    v-if="entry.user.id === currentUserId"
                                                    icon
                                                    size="large"
                                                    elevation="0"
                                                    color="yellow"
                                                    @click.stop="editEntry(entry.uid)"
                                                    density="compact"
                                                >
                                                    <v-icon>mdi-file-edit-outline</v-icon>
                                                </v-btn>
                                                <v-tooltip activator="parent" location="bottom">
                                                    {{ $t("entries.edit") }}
                                                </v-tooltip>
                                            </div>
                                            <div>
                                                <v-btn
                                                    v-if="entry.user.id === currentUserId"
                                                    icon
                                                    size="large"
                                                    elevation="0"
                                                    color="red"
                                                    @click.stop="confirmDelete(entry.uid)"
                                                    density="compact"
                                                >
                                                    <v-icon>mdi-delete</v-icon>
                                                    <v-tooltip activator="parent" location="bottom">
                                                        {{ $t("entries.delete") }}
                                                    </v-tooltip>
                                                </v-btn>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </div>
                            </template>

                            <template v-if="!$vuetify.display.smAndDown">
                                <v-toolbar-title class="text-body-2 font-weight-bold entry-card-toolbar-title">
                                    <div class="entry-toolbar-meta d-flex flex-wrap align-center ga-2">
                                        <template v-if="entry.identification_id">
                                            <span
                                                class="toolbar-left-group cursor-pointer"
                                                @click.stop="copyEntryId(entry.identification_id)"
                                                @mousedown.stop
                                                @touchstart.stop
                                            >
                                                <span class="text-white">ID:&nbsp;</span>
                                                <span class="text-yellow">
                                                    {{ entry.identification_id }}
                                                </span>
                                                <v-btn
                                                    icon
                                                    size="x-small"
                                                    density="compact"
                                                    variant="text"
                                                    color="yellow"
                                                    class="ml-1 mb-1"
                                                >
                                                    <v-icon size="16">
                                                        mdi-content-copy
                                                    </v-icon>
                                                </v-btn>
                                                <v-tooltip activator="parent" location="bottom">
                                                    {{ $t("entries.copyId") }}
                                                </v-tooltip>
                                            </span>
                                        </template>
                                        <template v-if="entry.identification_id && entry.distance_km != null">
                                            <span class="toolbar-item-separator"></span>
                                        </template>
                                        <template v-if="entry.distance_km != null">
                                            <span
                                                class="toolbar-left-group distance-toggle cursor-pointer"
                                                @click.stop="toggleRouteMap(entry.uid)"
                                                @mousedown.stop
                                                @touchstart.stop
                                            >
                                                <span class="text-white">{{ $t("entries.distance") }}:&nbsp;</span>
                                                <span class="text-yellow">{{ entry.distance_km }} km</span>
                                                <v-tooltip activator="parent" location="bottom">
                                                    {{
                                                        isRouteMapExpanded(entry.uid)
                                                            ? $t("entries.closeMap")
                                                            : $t("entries.openMap")
                                                    }}
                                                </v-tooltip>
                                            </span>
                                        </template>
                                        <template v-if="canShowReserveButton(entry)">
                                            <span
                                                v-if="entry.identification_id || entry.distance_km != null"
                                                class="toolbar-item-separator"
                                            ></span>
                                            <v-btn
                                                size="default"
                                                color="white"
                                                variant="flat"
                                                class="text-primary"
                                                @click.stop="openReservationModal(entry)"
                                            >
                                                {{ $t("entries.reserve") }}
                                            </v-btn>
                                        </template>
                                        <template
                                            v-if="
                                                entry.company &&
                                                (entry.identification_id ||
                                                    entry.distance_km != null ||
                                                    canShowReserveButton(entry))
                                            "
                                        >
                                            <span class="toolbar-item-separator"></span>
                                        </template>
                                        <template v-if="entry.company">
                                            <span
                                                class="toolbar-left-group cursor-pointer"
                                                @click.stop="goToCompanyIncidents(entry.company.id)"
                                                @mousedown.stop
                                                @touchstart.stop
                                            >
                                                <span class="text-white">{{ $t("entries.incidents") }}:&nbsp;</span>
                                                <span class="text-yellow">
                                                    {{ getTotalIncidents(entry) }}
                                                </span>
                                                <v-tooltip activator="parent" location="bottom">
                                                    <div>
                                                        {{ $t("entries.paymentIncidents") }}:
                                                        {{ getPaymentIncidentsCount(entry) }}
                                                    </div>
                                                    <div>
                                                        {{ $t("entries.transportIncidents") }}:
                                                        {{ getTransportIncidentsCount(entry) }}
                                                    </div>
                                                </v-tooltip>
                                            </span>
                                        </template>
                                        <template
                                            v-if="
                                                entry.identification_id ||
                                                entry.distance_km != null ||
                                                canShowReserveButton(entry) ||
                                                entry.company
                                            "
                                        >
                                            <span class="toolbar-item-separator"></span>
                                        </template>
                                        <EntryCalculatorsMenu
                                            variant="inline"
                                            :entry="entry"
                                            :show-duration="canShowTripDurationMenu(entry)"
                                            :duration-chip-label="getDurationChipLabel(entry)"
                                            :active-panel="getActiveCalculatorPanel(entry.uid)"
                                            :length-cm="getFloorAreaLength(entry.uid)"
                                            :width-cm="getFloorAreaWidth(entry.uid)"
                                            :package-count="getFloorAreaPackageCount(entry.uid)"
                                            :applied-floor-area-label="appliedFloorAreaByUid[entry.uid] ?? ''"
                                            :speed-kmh="getTripDurationSpeed(entry)"
                                            :price-per-liter="getFuelPrice(entry.uid)"
                                            :distance-km="getFuelDistance(entry)"
                                            :consumption-l-per100-km="getFuelConsumption(entry.uid)"
                                            :applied-fuel-cost-label="appliedFuelCostByUid[entry.uid] ?? ''"
                                            @update:active-panel="setActiveCalculatorPanel(entry.uid, $event)"
                                            @update:length-cm="setFloorAreaLength(entry.uid, $event)"
                                            @update:width-cm="setFloorAreaWidth(entry.uid, $event)"
                                            @update:package-count="setFloorAreaPackageCount(entry.uid, $event)"
                                            @update:speed-kmh="setCalculatorSpeed(entry.uid, $event)"
                                            @update:price-per-liter="setFuelPrice(entry.uid, $event)"
                                            @update:distance-km="setFuelDistance(entry.uid, $event)"
                                            @update:consumption-l-per100-km="setFuelConsumption(entry.uid, $event)"
                                            @apply-floor="applyFloorArea(entry, $event)"
                                            @apply-duration="applyTripDuration(entry, $event)"
                                            @apply-fuel="applyFuelCost(entry, $event)"
                                        />
                                    </div>
                                </v-toolbar-title>
                                <v-toolbar-items class="d-flex align-center">
                                    <div v-if="entry.company" class="d-flex align-center" @click.stop>
                                        <v-tooltip location="bottom">
                                            <template #activator="{ props: tooltipProps }">
                                                <span v-bind="tooltipProps" class="d-inline-flex align-center">
                                                    <star-rating-display
                                                        :model-value="entry.company.rating_average ?? 0"
                                                        :show-value="false"
                                                        :star-size="18"
                                                        star-color="#ffeb3b"
                                                        inactive-color="#616161"
                                                    />
                                                </span>
                                            </template>
                                            <template v-if="entry.company.rating_average != null">
                                                {{ $t("entries.companyRating") }}
                                                {{ entry.company.rating_average }}
                                            </template>
                                            <template v-else>
                                                {{ $t("entries.noCompanyRatingsYet") }}
                                            </template>
                                        </v-tooltip>
                                    </div>
                                    <span class="toolbar-item-separator"></span>
                                    <div>
                                        <span class="text-yellow text-body-2 font-weight-bold">{{ entry.view_count ?? 0 }}</span>
                                        <v-menu
                                            open-on-hover
                                            :close-delay="1500"
                                            :close-on-content-click="false"
                                            :scrim="false"
                                            location="bottom"
                                        >
                                            <template #activator="{ props: menuProps }">
                                                <v-btn
                                                    icon
                                                    size="large"
                                                    elevation="0"
                                                    color="yellow"
                                                    density="compact"
                                                    class="cursor-default"
                                                    v-bind="menuProps"
                                                    @mouseenter="fetchViewers(entry)"
                                                >
                                                    <v-icon>mdi-eye-outline</v-icon>
                                                </v-btn>
                                                <span class="toolbar-item-separator"></span>
                                            </template>
                                            <div class="viewers-menu-list">
                                                <template v-if="(viewersCache[entry.uid] || []).length">
                                                    <div
                                                        v-for="(viewer, index) in viewersCache[entry.uid]"
                                                        :key="index"
                                                        class="viewer-item text-body-2 py-1 px-4"
                                                        @click.stop="goToCompanyDetails(viewer.company_id)"
                                                    >
                                                        {{ [viewer.first_name, viewer.last_name].filter(Boolean).join(" ") || "-" }}{{ viewer.company ? `, ${viewer .company}` : "" }}
                                                    </div>
                                                </template>
                                                <div v-else class="viewer-item py-1 px-4">
                                                    {{ $t("entries.noViewers") }}
                                                </div>
                                            </div>
                                        </v-menu>
                                    </div>
                                    <div>
                                        <v-btn
                                            icon
                                            size="large"
                                            elevation="0"
                                            color="yellow"
                                            density="compact"
                                            class="cursor-default"
                                            @click.stop
                                        >
                                            <v-icon>mdi-information-outline</v-icon>
                                            <v-tooltip activator="parent" location="bottom">
                                                <div>
                                                    {{ $t("entries.createdAt") }}:
                                                    {{ formatLocalTime(entry.created_at) }}
                                                </div>
                                                <div>
                                                    {{ $t("entries.modifiedAt") }}:
                                                    {{ formatLocalTime(entry.modified_at) }}
                                                </div>
                                            </v-tooltip>
                                        </v-btn>
                                    </div>

                                    <div>
                                        <v-btn
                                            v-if="entry.user.id === currentUserId"
                                            icon
                                            size="large"
                                            elevation="0"
                                            color="yellow"
                                            @click.stop="editEntry(entry.uid)"
                                            density="compact"
                                        >
                                            <v-icon>mdi-file-edit-outline</v-icon>
                                        </v-btn>
                                        <v-tooltip activator="parent" location="bottom">
                                            {{ $t("entries.edit") }}
                                        </v-tooltip>
                                    </div>
                                    <div>
                                        <v-btn
                                            v-if="entry.user.id === currentUserId"
                                            icon
                                            size="large"
                                            elevation="0"
                                            color="red"
                                            @click.stop="confirmDelete(entry.uid)"
                                            density="compact"
                                        >
                                            <v-icon>mdi-delete</v-icon>
                                            <v-tooltip activator="parent" location="bottom">
                                                {{ $t("entries.delete") }}
                                            </v-tooltip>
                                        </v-btn>
                                    </div>
                                </v-toolbar-items>
                            </template>
                        </v-toolbar>

                        <!-- Card Content -->
                        <v-row class="d-flex justify-space-between">
                            <v-col cols="12" sm="12" md="10" lg="11">
                                <v-card-text class="text-body-1 pb-0">
                                    <v-row>
                                        <v-col cols="12">
                                            <strong style="font-size: 18px">
                                                <span class="entry-stop-group">
                                                    <template
                                                        v-for="(stop, stopIndex) in loadingStops(entry)"
                                                        :key="`load-${entry.uid}-${stop.id || stopIndex}`"
                                                    >
                                                        <img
                                                            v-if="stopIndex === 0"
                                                            :src="loadingPng"
                                                            class="entry-icon-big"
                                                            alt=""
                                                            :style="{ gridRow: 1, gridColumn: 1 }"
                                                        />
                                                        <span
                                                            v-if="stopIndex === 0"
                                                            :style="{ gridRow: 1, gridColumn: 2 }"
                                                        >{{ $t("entries.loadingFrom") }}</span>
                                                        <span :style="{ gridRow: stopIndex + 1, gridColumn: 3 }">
                                                            <template v-if="loadingStops(entry).length > 1">{{ stopIndex + 1 }}. </template>
                                                            {{ stop.countryName?.name }},
                                                            {{ stop.cityName }},
                                                            {{ stop.postal_code }}
                                                            |
                                                            {{ formatDateRange(stop.date_start, stop.date_end) }}
                                                            <span v-if="stop.time_start || stop.time_end">
                                                                {{ $t("entries.betweenHours") }}
                                                                {{ stop.time_start?.slice(0, 5) }}
                                                                -
                                                                {{ stop.time_end?.slice(0, 5) }}
                                                            </span>
                                                        </span>
                                                    </template>
                                                    <template
                                                        v-for="(stop, stopIndex) in unloadingStops(entry)"
                                                        :key="`unload-${entry.uid}-${stop.id || stopIndex}`"
                                                    >
                                                        <img
                                                            v-if="stopIndex === 0"
                                                            :src="unloadingPng"
                                                            class="entry-icon-big"
                                                            alt=""
                                                            :style="{ gridRow: loadingStops(entry).length + 1, gridColumn: 1 }"
                                                        />
                                                        <span
                                                            v-if="stopIndex === 0"
                                                            :style="{ gridRow: loadingStops(entry).length + 1, gridColumn: 2 }"
                                                        >{{ $t("entries.destination") }}</span>
                                                        <span
                                                            :style="{
                                                                gridRow: loadingStops(entry).length + stopIndex + 1,
                                                                gridColumn: 3,
                                                            }"
                                                        >
                                                            <template v-if="unloadingStops(entry).length > 1">{{ stopIndex + 1 }}. </template>
                                                            {{ stop.countryName?.name }},
                                                            {{ stop.cityName }},
                                                            {{ stop.postal_code }}
                                                            |
                                                            {{ formatDateRange(stop.date_start, stop.date_end) }}
                                                            <span v-if="stop.time_start || stop.time_end">
                                                                {{ $t("entries.betweenHours") }}
                                                                {{ stop.time_start?.slice(0, 5) }}
                                                                -
                                                                {{ stop.time_end?.slice(0, 5) }}
                                                            </span>
                                                        </span>
                                                    </template>
                                                </span>
                                                <!-- GET DIRECTIONS -->
                                                <RouteMap
                                                    :data-cy-prefix="`entry-${entry.uid}`"
                                                    :origin-place-id="
                                                        loadingStops(entry)[0]?.place_id
                                                    "
                                                    :destination-place-id="
                                                        unloadingStops(entry)[
                                                            unloadingStops(entry).length - 1
                                                        ]?.place_id
                                                    "
                                                    :show-button="false"
                                                    :ref="
                                                        (routeMap) =>
                                                            setRouteMapRef(
                                                                entry.uid,
                                                                routeMap
                                                            )
                                                    "
                                                />
                                                <!-- TRANSPORT REGIME -->
                                                {{
                                                    $t(
                                                        "entries.transportRegime"
                                                    )
                                                }}
                                                {{
                                                    entry.transport_options
                                                        ?.truck_transport_type ||
                                                    "N/A"
                                                }},

                                                {{
                                                    entry.transport_options
                                                        ?.truck_coating ||
                                                    "N/A"
                                                }},

                                                <span
                                                    v-if="
                                                        entry.form_type ===
                                                        'ware'
                                                    "
                                                >
                                                    {{
                                                        entry.transport_options
                                                            ?.platform_type ||
                                                        "N/A"
                                                    }}
                                                </span>
                                                <span
                                                    v-if="
                                                        entry.transport_options
                                                            ?.dangerous_load
                                                    "
                                                >
                                                    {{
                                                        $t(
                                                            "formFields.dangerousLoad"
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    v-if="
                                                        entry.form_type ===
                                                            'ware' &&
                                                        entry.transport_options
                                                            ?.escrow_account ===
                                                            true
                                                    "
                                                    >, Escrow
                                                </span>

                                                <span
                                                    v-if="
                                                        entry.form_type ===
                                                        'truck'
                                                    "
                                                >
                                                    {{
                                                        entry.transport_options
                                                            ?.truck_type ||
                                                        "N/A"
                                                    }}
                                                </span>

                                                <span
                                                    v-if="
                                                        formatSpecialFeatures(
                                                            entry.transport_options
                                                        )
                                                    "
                                                    >,
                                                    {{
                                                        formatSpecialFeatures(
                                                            entry.transport_options
                                                        )
                                                    }}
                                                </span>
                                                <br />

                                                <!-- TRUCK SPECS -->
                                                {{ $t("entries.weight") }}
                                                {{ entry.info.weight }}t.
                                                <span v-if="entry.info.volume">
                                                    {{ $t("entries.volume") }}
                                                    {{ entry.info.volume }}m³.
                                                </span>
                                                {{ $t("entries.floorAreaUnit") }}:
                                                {{ entry.info.mdp }}m.

                                                <!-- PRICE -->
                                                <span v-if="entry.info.price">
                                                    <br />
                                                    {{ $t("entries.price") }}
                                                    {{ entry.info.price }}
                                                    {{ entry.info.currency }}
                                                    <span
                                                        v-if="
                                                            entry.info
                                                                .vat_type ===
                                                            true
                                                        "
                                                        >{{
                                                            $t(
                                                                "formFields.withVAT"
                                                            )
                                                        }}&nbsp;</span
                                                    >
                                                    <span v-else
                                                        >{{
                                                            $t(
                                                                "formFields.withoutVAT"
                                                            )
                                                        }}&nbsp;</span
                                                    ></span
                                                >
                                                <!-- PAYMENT TERM -->
                                                {{ $t("entries.paymentTerm") }}
                                                {{ entry.info.payment_term }}
                                                <template v-if="entry.notes">
                                                    <br />
                                                    <br />
                                                    <span
                                                        class="text-success d-inline-flex align-center"
                                                    >
                                                        {{
                                                            $t(
                                                                "formFields.observations"
                                                            )
                                                        }}
                                                        <v-icon
                                                            class="mx-1"
                                                            icon="mdi-arrow-right-bold"
                                                            size="18"
                                                            color="success"
                                                        />
                                                        {{ entry.notes }}
                                                    </span>
                                                </template>
                                            </strong>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-col>

                            <!-- Action Buttons -->
                            <v-col class="pt-0" cols="12" sm="12" md="2" lg="1">
                                <v-card-actions
                                    :class="
                                        $vuetify.display.mdAndUp
                                            ? 'flex-column align-right ga-2 pt-6 pr-2 ml-n4'
                                            : 'flex-row flex-wrap ga-5 justify-center pt-0 pb-4'
                                    "
                                >
                                    <!-- Refresh Button -->
                                    <template
                                        v-if="
                                            entry.form_type &&
                                            entry.user.id === currentUserId
                                        "
                                    >
                                        <div class="d-flex align-center">
                                            <v-btn
                                                icon
                                                size="x-large"
                                                elevation="3"
                                                color="yellow"
                                                @click.stop="
                                                    refreshEntry(entry.uid)
                                                "
                                                density="compact"
                                            >
                                                <v-icon>mdi-refresh</v-icon>
                                                <v-tooltip
                                                    location="left"
                                                    activator="parent"
                                                >
                                                    {{ $t("entries.refresh") }}
                                                </v-tooltip>
                                            </v-btn>
                                        </div>
                                    </template>

                                    <MessagingButton
                                        :uid="entry.uid"
                                        :formType="entry.form_type"
                                        :userHasApp="entry.user?.hasWhatsApp"
                                        :phone="
                                            formatPhone(
                                                entry.user?.phone_prefix,
                                                entry.user?.phone
                                            )
                                        "
                                        color="green"
                                        icon="mdi-whatsapp"
                                        iconType="mdi"
                                        :tooltipText="
                                            $t('entries.infoMessageWhatsapp')
                                        "
                                        :primaryAction="
                                            (phone) =>
                                                openWhatsApp(
                                                    phone,
                                                    entryWhatsAppPrefillMessage(entry)
                                                )
                                        "
                                        :secondaryAction="openWhatsAppDownload"
                                        :secondaryActionLabel="
                                            $t('entries.downloadWhatsapp')
                                        "
                                    />

                                    <MessagingButton
                                        :uid="entry.uid"
                                        :formType="entry.form_type"
                                        :userHasApp="entry.user?.hasSignalApp"
                                        :phone="
                                            formatPhone(
                                                entry.user?.phone_prefix,
                                                entry.user?.phone
                                            )
                                        "
                                        color="blue"
                                        :icon="signalIcon"
                                        iconType="img"
                                        :tooltipText="
                                            $t('entries.infoMessageSignal')
                                        "
                                        :primaryAction="
                                            (phone) => openSignal(phone)
                                        "
                                        :secondaryAction="openSignalDownload"
                                        :secondaryActionLabel="
                                            $t('entries.downloadSignal')
                                        "
                                    />
                                    <div v-if="canShowMarkTruckFullOrAvailableButton(entry)" class="d-flex align-center" :class="{ 'mark-available-btn': entry.is_full }">
                                        <v-btn
                                            icon
                                            size="x-large"
                                            elevation="3"
                                            :color="entry.is_full ? 'green' : 'orange'"
                                            density="compact"
                                            @click.stop="entry.is_full ? openMarkTruckAvailableConfirm(entry) : openMarkTruckFullConfirm(entry)"
                                        >
                                            <v-icon>{{ entry.is_full ? 'mdi-truck-check' : 'mdi-truck-cargo-container' }}</v-icon>
                                            <v-tooltip location="left" activator="parent">
                                                {{ entry.is_full ? $t('entries.markTruckAvailable') : $t('entries.markTruckFull') }}
                                            </v-tooltip>
                                        </v-btn>
                                    </div>
                                    <div class="d-flex align-center">
                                        <v-btn
                                            icon
                                            size="x-large"
                                            elevation="3"
                                            color="yellow"
                                            density="compact"
                                            @click.stop="toggleRouteMap(entry.uid)"
                                        >
                                            <img
                                                :src="googleIcon"
                                                alt="Google Maps"
                                                class="google-maps-icon"
                                            />
                                            <v-tooltip
                                                location="left"
                                                activator="parent"
                                            >
                                                {{
                                                    isRouteMapExpanded(entry.uid)
                                                        ? $t("entries.closeMap")
                                                        : $t("entries.openMap")
                                                }}
                                            </v-tooltip>
                                        </v-btn>
                                    </div>
                                </v-card-actions>
                            </v-col>
                        </v-row>

                        <!-- EXPANDED DETAILS (User + Company info) -->

                        <v-row
                            v-if="expandedEntryUid === entry.uid"
                            data-cy="entry-expanded"
                            class="px-4 pb-4"
                        >
                            <v-col cols="12">
                                <v-row>
                                    <!-- User Info -->
                                    <v-col cols="12" md="6">
                                        <h4
                                            class="text-subtitle-2 font-weight-bold mb-2"
                                        >
                                            {{ $t("entries.postedBy") }}
                                        </h4>
                                        <p>
                                            <strong
                                                >{{
                                                    $t("entries.name")
                                                }}:</strong
                                            >
                                            {{ entry.user.first_name }}
                                            {{ entry.user.last_name }}<br />

                                            <strong
                                                >{{
                                                    $t("company.email")
                                                }}:</strong
                                            >
                                            <a
                                                :href="`mailto:${entry.user.email}`"
                                            >
                                                {{ entry.user.email }} </a
                                            ><br />

                                            <strong
                                                >{{
                                                    $t("company.phone")
                                                }}:</strong
                                            >
                                            {{
                                                formatPhone(
                                                    entry.user.phone_prefix,
                                                    entry.user.phone
                                                )
                                            }}<br />
                                        </p>
                                        <p
                                            class="d-flex align-center flex-wrap"
                                        >
                                            <strong
                                                >{{
                                                    $t("entries.speaks")
                                                }}:</strong
                                            >
                                            <span
                                                v-for="lang in entry.user
                                                    .spoken_languages"
                                                :key="lang"
                                                class="ml-2"
                                            >
                                                <img
                                                    :src="`${$baseUrl}country-flags/${languageToFlag[lang]}`"
                                                    :alt="lang"
                                                    width="24"
                                                    height="24"
                                                />
                                            </span>
                                        </p>
                                    </v-col>

                                    <!-- Company Info -->
                                    <v-col
                                        cols="12"
                                        md="6"
                                        class="clickable-company-info"
                                        @mousedown.stop
                                        @touchstart.stop
                                    >
                                        <router-link
                                            :to="{
                                                name: 'CompanyDetails',
                                                params: {
                                                    id: entry.company.id,
                                                },
                                            }"
                                            class="company-router-link"
                                        >
                                            <div>
                                                <h4
                                                    class="text-subtitle-2 font-weight-bold mb-2"
                                                >
                                                    {{
                                                        $t(
                                                            "company.cardFirmaTitle"
                                                        )
                                                    }}
                                                </h4>
                                                <v-row>
                                                    <v-col
                                                        cols="12"
                                                        :md="
                                                            entry.company
                                                                .is_trusted
                                                                ? 8
                                                                : 12
                                                        "
                                                    >
                                                        <p>
                                                            <strong
                                                                >{{
                                                                    $t(
                                                                        "company.memberID"
                                                                    )
                                                                }}:</strong
                                                            >
                                                            {{
                                                                entry.company
                                                                    .id
                                                            }}<br />
                                                            <strong
                                                                >{{
                                                                    $t(
                                                                        "entries.name"
                                                                    )
                                                                }}:</strong
                                                            >
                                                            {{
                                                                entry.company
                                                                    .name
                                                            }}<br />
                                                            <strong
                                                                >{{
                                                                    $t(
                                                                        "company.registrationNumber"
                                                                    )
                                                                }}:</strong
                                                            >
                                                            {{
                                                                entry.company
                                                                    .registry_combination
                                                            }}<br />
                                                            <strong
                                                                >{{
                                                                    $t(
                                                                        "company.VAT"
                                                                    )
                                                                }}:</strong
                                                            >
                                                            {{
                                                                entry.company
                                                                    .VATNumber
                                                            }}<br />
                                                            <strong
                                                                v-if="
                                                                    entry
                                                                        .company
                                                                        .fax
                                                                "
                                                                >{{
                                                                    $t(
                                                                        "company.fax"
                                                                    )
                                                                }}:</strong
                                                            >
                                                            {{
                                                                entry.company
                                                                    .fax
                                                            }}<br />
                                                            <strong
                                                                v-if="
                                                                    entry
                                                                        .company
                                                                        .website
                                                                "
                                                                >{{
                                                                    $t(
                                                                        "register.website"
                                                                    )
                                                                }}:</strong
                                                            >
                                                            <a
                                                                :href="
                                                                    entry
                                                                        .company
                                                                        .website
                                                                "
                                                                target="_blank"
                                                                @click.stop
                                                                >{{
                                                                    entry
                                                                        .company
                                                                        .website
                                                                }}</a
                                                            >
                                                        </p>
                                                    </v-col>
                                                    <v-col
                                                        v-if="
                                                            entry.company
                                                                .is_trusted
                                                        "
                                                    >
                                                        <TrustedCompanyBadge
                                                            :href="entry.company.company_badge_url || ''"
                                                        />
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </router-link>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-expand-transition>
                        </div>
                    </template>
                    {{ entryReservedTooltip(entry) }}
                </v-tooltip>
            </v-col>
        </v-row>

            <ListPagination
                class="mt-6"
                v-model:page="page"
                v-model:items-per-page="pageSize"
                :total-items="filteredAndSortedEntries.length"
                :items-per-page-options="pageSizeOptions"
            />
        </div>

        <ConfirmDialog
            :visible="!!entryToMarkFull"
            :title="$t('entries.confirmMarkTruckFullTitle')"
            :message="$t('entries.confirmMarkTruckFullMessage')"
            :primary-text="$t('common.yes')"
            :secondary-text="$t('common.no')"
            secondary-color="error"
            @update:visible="(v) => { if (!v) entryToMarkFull = null; }"
            @primary="confirmMarkTruckFull"
            @secondary="entryToMarkFull = null"
        />
        <ConfirmDialog
            :visible="!!entryToMarkAvailable"
            :title="$t('entries.confirmMarkTruckAvailableTitle')"
            :message="$t('entries.confirmMarkTruckAvailableMessage')"
            :primary-text="$t('common.yes')"
            :secondary-text="$t('common.no')"
            secondary-color="error"
            @update:visible="(v) => { if (!v) entryToMarkAvailable = null; }"
            @primary="confirmMarkTruckAvailable"
            @secondary="entryToMarkAvailable = null"
        />

        <!-- Reservation Modal -->
        <v-dialog
            v-model="reservationModalVisible"
            max-width="860"
            persistent
        >
            <v-card
                ref="reserveDialogCard"
                class="d-flex flex-column overflow-hidden"
                :style="reserveDialogCardStyle"
            >
                <v-card-title class="flex-shrink-0">{{ $t('entries.reserve') }}</v-card-title>
                <v-card-text class="reserve-dialog-body d-flex flex-column flex-grow-1 overflow-hidden min-h-0">
                    <ReservationForm
                        v-if="reservationEntryForModal"
                        ref="reservationFormRef"
                        :entry-type="reservationEntryForModal.form_type"
                        :entry-info="reservationEntryForModal.info || null"
                        :loading-locations="loadingStops(reservationEntryForModal)"
                        :unloading-locations="unloadingStops(reservationEntryForModal)"
                        @submit="submitReservation"
                        @cancel="reservationModalVisible = false"
                    />
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from "axios";
import { formatLocalTime, formatDateRange, formatDateOnly, showAlertModal, entryExpiresWithin, getReservationCreateErrorKey, formatDurationCompact, entryLoadingStops, entryUnloadingStops } from "@/utils";
import {
    buildEntryWhatsAppContactMessage,
    countryDisplayName,
    openSignalChat,
    openSignalDownloadPage,
    openWhatsAppChat,
    openWhatsAppDownloadPage,
} from "@/utils/messagingContact";
import { computeEtaBreakdown } from "@/utils/etaCalculation";
import signalIcon from "@/assets/signal.svg";
import googleIcon from "@/assets/google.svg";
import emitter from "@/eventBus";
import { mapActions, mapState } from "vuex";
import MessagingButton from "@/components/MessagingButton.vue";
import {
    PAYMENT_DEADLINE,
    TRUCK_COATING,
    TRUCK_TRANSPORT_TYPE,
    TRUCK_SPECIAL_FEATURE,
    TRUCK_TYPE,
    PLATFORM_TYPE,
} from "@/enums/truckEnums.js";
import ConfirmDialog from "@/components/display/ConfirmDialog.vue";
import EntriesFilterBar from "@/components/display/EntriesFilterBar.vue";
import ListPagination from "@/components/display/ListPagination.vue";
import TrustedCompanyBadge from "@/components/display/TrustedCompanyBadge.vue";
import RouteMap from "@/components/forms/RouteMap.vue";
import EntryCalculatorsMenu from "@/components/entries/EntryCalculatorsMenu.vue";
import ReservationForm from "@/components/reservation/ReservationForm.vue";
import StarRatingDisplay from "@/components/rating/StarRatingDisplay.vue";
import loadingPng from "@/assets/Loading.png";
import unloadingPng from "@/assets/Unloading.png";
import truckPng from "@/assets/Truck.png";
import warePng from "@/assets/Ware.png";

const PAGE_SIZE_OPTIONS = [10, 50, 100];

function getSessionUserId() {
    try {
        const user = JSON.parse(sessionStorage.getItem("user") || "{}");
        return user?.id ?? null;
    } catch {
        return null;
    }
}

function getEntriesPageSizeStorageKey(userId) {
    return `app:entries-page-size:${userId}`;
}

function parsePageSize(raw) {
    const saved = parseInt(raw, 10);
    return PAGE_SIZE_OPTIONS.includes(saved) ? saved : null;
}

function readSavedPageSize() {
    const userId = getSessionUserId();
    if (!userId) return 10;

    const saved = parsePageSize(
        localStorage.getItem(getEntriesPageSizeStorageKey(userId))
    );
    return saved ?? 10;
}

function savePageSize(pageSize) {
    const userId = getSessionUserId();
    if (!userId) return;
    localStorage.setItem(getEntriesPageSizeStorageKey(userId), String(pageSize));
}

export default {
    components: {
        ConfirmDialog,
        EntriesFilterBar,
        ListPagination,
        MessagingButton,
        RouteMap,
        EntryCalculatorsMenu,
        ReservationForm,
        StarRatingDisplay,
        TrustedCompanyBadge,
    },
    props: {
        // When true, fetch only entries for the logged-in user
        userEntries: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            truckPng,
            warePng,
            loadingPng,
            unloadingPng,
            languageToFlag: {
                ro: "Flag-Romania.svg",
                hu: "Flag-Hungary.svg",
                en: "Flag-Great Britain.svg",
                de: "Flag-Germany.svg",
                fr: "Flag-France.svg",
                bg: "Flag-Bulgaria.svg",
                pl: "Flag-Poland.svg",
                sr: "Flag-Serbia.svg",
                hr: "Flag-Croatia.svg",
                sl: "Flag-Slovenia.svg",
                sk: "Flag-Slovakia.svg",
                it: "Flag-Italy.svg",
            },
            entries: [],
            signalIcon,
            googleIcon,
            expandedEntryUid: null,
            filters: {
                loadingLocation: {
                    country_code: null,
                    place_id: null,
                },
                unloadingLocation: {
                    country_code: null,
                    place_id: null,
                },
                weight: null,
                mdp: null,
                expiresIn: null,
                identificationId: null,
                showReservedWareOnly: false,
            },
            sortDirection: "desc",
            highlightUid: this.$route.params.uid || null,
            routeMapRefs: {},
            routeMapExpandedByUid: {},
            activeCalculatorPanelByUid: {},
            calculatorClosedAtByUid: {},
            floorAreaLengthByUid: {},
            floorAreaWidthByUid: {},
            floorAreaPackageCountByUid: {},
            fuelPriceByUid: {},
            fuelDistanceByUid: {},
            fuelConsumptionByUid: {},
            calculatorSpeedByUid: {},
            appliedTripDurationByUid: {},
            appliedFloorAreaByUid: {},
            appliedFuelCostByUid: {},
            viewersCache: {},
            reservationModalVisible: false,
            reservationEntryForModal: null,
            reserveDialogMaxHeight: null,
            entryToMarkFull: null,
            entryToMarkAvailable: null,
            page: 1,
            pageSize: readSavedPageSize(),
            pageSizeOptions: PAGE_SIZE_OPTIONS,
        };
    },
    created() {
        this.applyRouteQueryFilters();
    },
    async mounted() {
        let localStorageLanguage = localStorage.getItem("selectedLanguage");
        if (localStorageLanguage) {
            this.$store.state.selectedLanguage = localStorageLanguage;
        }

        await this.fetchCountries();
        // await this.fetchCityLookup();
        await this.fetchEntries(); // Fetch entries

        emitter.on("language-changed", async (newLang) => {
            this.$store.state.selectedLanguage = newLang;
            await this.fetchCountries();
            await this.fetchEntries();
        });

        emitter.on("ws-update", this.handleWsUpdate);
    },
    unmounted() {
        // Clean up the event listener
        emitter.off("ws-update", this.handleWsUpdate);
        emitter.off("language-changed");
    },
    methods: {
        loadingStops(entry) {
            return entryLoadingStops(entry);
        },
        unloadingStops(entry) {
            return entryUnloadingStops(entry);
        },
        extraStopCount(entry, role) {
            const stops = role === "loading" ? this.loadingStops(entry) : this.unloadingStops(entry);
            return Math.max(0, (stops || []).length - 1);
        },
        extraStopTooltip(entry, role) {
            const count = this.extraStopCount(entry, role);
            const singular = role === "loading"
                ? "entries.moreLoadingLocation"
                : "entries.moreUnloadingLocation";
            const plural = role === "loading"
                ? "entries.moreLoadingLocations"
                : "entries.moreUnloadingLocations";
            return this.$t(count === 1 ? singular : plural, { count });
        },
        stopMatches(stops, countryCode, placeId) {
            return (stops || []).some((stop) => {
                if (countryCode && stop?.country_code !== countryCode) return false;
                if (placeId && stop?.place_id !== placeId) return false;
                return true;
            });
        },
        measureReserveDialog() {
            this.reserveDialogMaxHeight = null;
            this.$nextTick(() => {
                requestAnimationFrame(() => this.applyReserveDialogCap(0, 0));
            });
        },
        applyReserveDialogCap(attempt, previousFormHeight) {
            const entry = this.reservationEntryForModal;
            const cardComponent = this.$refs.reserveDialogCard;
            const cardEl = cardComponent?.$el || cardComponent;
            if (!entry || !cardEl || !this.reservationModalVisible) return;
            const extraRows =
                Math.max(0, this.loadingStops(entry).length - 1) +
                Math.max(0, this.unloadingStops(entry).length - 1);
            if (extraRows <= 0) {
                this.reserveDialogMaxHeight = null;
                return;
            }
            const form = cardEl.querySelector("form");
            const title = cardEl.querySelector(".v-card-title");
            const body = cardEl.querySelector(".reserve-dialog-body");
            const sampleRow = cardEl.querySelector(".reserve-stop-row");
            const formHeight = form?.scrollHeight || 0;
            const laidOut =
                sampleRow &&
                title &&
                body &&
                formHeight > 200 &&
                formHeight === previousFormHeight;
            if (!laidOut) {
                if (attempt < 40) {
                    setTimeout(() => this.applyReserveDialogCap(attempt + 1, formHeight), 50);
                }
                return;
            }
            const rowStyle = window.getComputedStyle(sampleRow);
            const rowHeight =
                sampleRow.offsetHeight +
                (parseFloat(rowStyle.marginTop) || 0) +
                (parseFloat(rowStyle.marginBottom) || 0);
            const titleStyle = window.getComputedStyle(title);
            const bodyStyle = window.getComputedStyle(body);
            const chrome =
                title.offsetHeight +
                (parseFloat(titleStyle.marginTop) || 0) +
                (parseFloat(titleStyle.marginBottom) || 0) +
                (parseFloat(bodyStyle.paddingTop) || 0) +
                (parseFloat(bodyStyle.paddingBottom) || 0);
            const naturalHeight = chrome + formHeight - extraRows * rowHeight;
            if (naturalHeight > 200) {
                this.reserveDialogMaxHeight = `${Math.ceil(naturalHeight)}px`;
            }
        },
        showAlertModal,
        canShowTripDurationMenu(entry) {
            if (entry.distance_km == null) return false;
            if (entry.form_type === "ware") return true;
            if (entry.form_type === "truck") {
                const speed = Number(entry.info?.avg_speed_kmh);
                return Number.isFinite(speed) && speed > 0;
            }
            return false;
        },
        getTripDurationSpeed(entry) {
            if (entry.form_type === "truck") {
                return entry.info?.avg_speed_kmh ?? "";
            }
            return this.calculatorSpeedByUid[entry.uid] ?? "";
        },
        getSmallCardTripDurationLabel(entry) {
            if (entry.distance_km == null) return null;

            if (entry.form_type === "truck") {
                const speed = Number(entry.info?.avg_speed_kmh);
                if (!Number.isFinite(speed) || speed <= 0) return null;

                const breakdown = computeEtaBreakdown({
                    distanceKm: entry.distance_km,
                    avgSpeedKmh: speed,
                });
                if (!breakdown) return null;

                return formatDurationCompact(breakdown.totalTripSeconds);
            }

            return this.appliedTripDurationByUid[entry.uid] ?? null;
        },
        getActiveCalculatorPanel(uid) {
            return this.activeCalculatorPanelByUid[uid] ?? null;
        },
        setActiveCalculatorPanel(uid, panel) {
            const previous = this.activeCalculatorPanelByUid[uid] ?? null;
            this.activeCalculatorPanelByUid = {
                ...this.activeCalculatorPanelByUid,
                [uid]: panel,
            };
            // Outside-click closes the calculator, then the same click hits the card.
            // Remember the close briefly so we don't collapse the big card too.
            if (previous && !panel) {
                this.calculatorClosedAtByUid = {
                    ...this.calculatorClosedAtByUid,
                    [uid]: Date.now(),
                };
            }
        },
        wasCalculatorJustClosed(uid, withinMs = 400) {
            const closedAt = this.calculatorClosedAtByUid[uid];
            return !!closedAt && Date.now() - closedAt < withinMs;
        },
        getDurationChipLabel(entry) {
            if (!this.canShowTripDurationMenu(entry)) return "";
            if (entry.form_type === "truck") {
                return this.getSmallCardTripDurationLabel(entry) || "";
            }
            return this.appliedTripDurationByUid[entry.uid] ?? "";
        },
        getSmallCardEstimateRows(entry) {
            const rows = [];
            const floorLabel = this.appliedFloorAreaByUid[entry.uid];
            if (floorLabel) {
                rows.push({
                    key: "floor",
                    icon: "mdi-ruler-square",
                    value: `${floorLabel} ${this.$t("entries.floorAreaUnit")}`,
                    tooltip: this.$t("entries.floorAreaAppliedTooltip"),
                });
            }

            const durationLabel = this.getDurationChipLabel(entry);
            if (durationLabel) {
                rows.push({
                    key: "duration",
                    icon: "mdi-timer-outline",
                    value: durationLabel,
                    tooltip: this.$t("entries.tripDurationTooltip"),
                });
            }

            const fuelLabel = this.appliedFuelCostByUid[entry.uid];
            if (fuelLabel) {
                rows.push({
                    key: "fuel",
                    icon: "mdi-gas-station",
                    value: fuelLabel,
                    tooltip: this.$t("entries.fuelCalculatorAppliedTooltip"),
                });
            }

            return rows;
        },
        getFuelPrice(uid) {
            return this.fuelPriceByUid[uid] ?? "";
        },
        setFuelPrice(uid, value) {
            this.fuelPriceByUid = {
                ...this.fuelPriceByUid,
                [uid]: value,
            };
        },
        getFuelDistance(entry) {
            const local = this.fuelDistanceByUid[entry.uid];
            if (local != null && local !== "") return local;
            if (entry.distance_km != null && entry.distance_km !== "") {
                return String(entry.distance_km);
            }
            return "";
        },
        setFuelDistance(uid, value) {
            this.fuelDistanceByUid = {
                ...this.fuelDistanceByUid,
                [uid]: value,
            };
        },
        getFuelConsumption(uid) {
            return this.fuelConsumptionByUid[uid] ?? "";
        },
        setFuelConsumption(uid, value) {
            this.fuelConsumptionByUid = {
                ...this.fuelConsumptionByUid,
                [uid]: value,
            };
        },
        applyFuelCost(entry, payload) {
            if (!payload?.resultLabel) return;

            this.setFuelPrice(entry.uid, payload.pricePerLiter);
            this.setFuelDistance(entry.uid, payload.distanceKm);
            this.setFuelConsumption(entry.uid, payload.consumptionLPer100Km);
            this.appliedFuelCostByUid = {
                ...this.appliedFuelCostByUid,
                [entry.uid]: payload.resultLabel,
            };
            this.setActiveCalculatorPanel(entry.uid, null);
        },
        getFloorAreaLength(uid) {
            return this.floorAreaLengthByUid[uid] ?? "";
        },
        setFloorAreaLength(uid, value) {
            this.floorAreaLengthByUid = {
                ...this.floorAreaLengthByUid,
                [uid]: value,
            };
        },
        getFloorAreaWidth(uid) {
            return this.floorAreaWidthByUid[uid] ?? "";
        },
        setFloorAreaWidth(uid, value) {
            this.floorAreaWidthByUid = {
                ...this.floorAreaWidthByUid,
                [uid]: value,
            };
        },
        getFloorAreaPackageCount(uid) {
            return this.floorAreaPackageCountByUid[uid] ?? "1";
        },
        setFloorAreaPackageCount(uid, value) {
            this.floorAreaPackageCountByUid = {
                ...this.floorAreaPackageCountByUid,
                [uid]: value,
            };
        },
        getCalculatorSpeed(uid) {
            return this.calculatorSpeedByUid[uid] ?? "";
        },
        setCalculatorSpeed(uid, value) {
            this.calculatorSpeedByUid = {
                ...this.calculatorSpeedByUid,
                [uid]: value,
            };
        },
        applyTripDuration(entry, payload) {
            const speed =
                typeof payload === "object" ? payload.speedKmh : payload;
            const speedNum = Number(speed);
            if (!Number.isFinite(speedNum) || speedNum <= 0) return;

            this.setCalculatorSpeed(entry.uid, speed);

            const breakdown = computeEtaBreakdown({
                distanceKm: entry.distance_km,
                avgSpeedKmh: speedNum,
            });
            if (!breakdown) return;

            const label =
                typeof payload === "object" && payload.resultLabel
                    ? payload.resultLabel
                    : formatDurationCompact(breakdown.totalTripSeconds);
            if (!label) return;

            this.appliedTripDurationByUid = {
                ...this.appliedTripDurationByUid,
                [entry.uid]: label,
            };
            this.setActiveCalculatorPanel(entry.uid, null);
        },
        applyFloorArea(entry, payload) {
            const resultLabel = payload?.resultLabel;
            if (!resultLabel) return;

            this.setFloorAreaLength(entry.uid, payload.lengthCm);
            this.setFloorAreaWidth(entry.uid, payload.widthCm);
            this.setFloorAreaPackageCount(entry.uid, payload.packageCount);

            this.appliedFloorAreaByUid = {
                ...this.appliedFloorAreaByUid,
                [entry.uid]: resultLabel,
            };
            this.setActiveCalculatorPanel(entry.uid, null);
        },
        applyRouteQueryFilters() {
            const q = this.$route.query;
            if (q.expiresIn === "4h" || q.expiresIn === "6h" || q.expiresIn === "1d") {
                this.filters.expiresIn = q.expiresIn;
            } else {
                this.filters.expiresIn = null;
            }
            this.filters.identificationId = q.identificationId || null;
            this.filters.showReservedWareOnly =
                q.showReservedWareOnly === "1" || q.showReservedWareOnly === "true";
        },
        formatPhone(prefix, phone) {
            // Ensure we have both parts
            if (!phone) return "";
            if (!prefix) {
                // fallback: if the phone already starts with '+' then leave it
                return phone.startsWith("+") ? phone : `+${phone}`;
            }
            // Normalise: make sure prefix starts with '+'
            const cleanPrefix = prefix.startsWith("+") ? prefix : `+${prefix}`;
            // Avoid double-plus if phone already has it
            const cleanPhone = phone.replace(/^\+/, "");
            return `${cleanPrefix}${cleanPhone}`;
        },
        /**
         * Returns a comma-separated string of special features,
         * or an empty string if none exist.
         */
        formatSpecialFeatures(opts) {
            if (!opts) return "";

            const feat = opts.truck_special_features;

            // Already a translated, comma-separated string
            if (typeof feat === "string" && feat.trim().length) {
                return feat;
            }

            // Fallback to legacy scalar
            return opts.truck_special_feature || "";
        },
        toggleSort() {
            this.sortDirection = this.sortDirection === "desc" ? "asc" : "desc";
        },
        isMobile() {
            return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
        },
        entryWhatsAppPrefillMessage(entry) {
            if (!entry) return "";
            const loading = entry.loading_location || {};
            const unloading = entry.unloading_location || {};
            return buildEntryWhatsAppContactMessage({
                formType: entry.form_type,
                id: entry.identification_id || entry.uid || "",
                fromCountry:
                    loading.countryName?.name ||
                    countryDisplayName(this.countries, loading.countryName?.code),
                fromCity: loading.cityName || loading.city || "",
                fromDateStart: formatDateOnly(loading.date_start),
                fromDateEnd: formatDateOnly(loading.date_end),
                toCountry:
                    unloading.countryName?.name ||
                    countryDisplayName(this.countries, unloading.countryName?.code),
                toCity: unloading.cityName || unloading.city || "",
                toDateStart: formatDateOnly(unloading.date_start),
                toDateEnd: formatDateOnly(unloading.date_end),
            });
        },
        openWhatsApp(phone, prefillMessage = "") {
            openWhatsAppChat(phone, prefillMessage);
        },
        openWhatsAppDownload() {
            openWhatsAppDownloadPage();
        },
        openSignal(phone) {
            openSignalChat(phone);
        },
        openSignalDownload() {
            openSignalDownloadPage();
        },
        /**
         * Store or clear a RouteMap component instance reference by entry uid.
         *
         * @param {string} uid - Entry unique identifier.
         * @param {object|null} routeMap - RouteMap instance when mounted, or null when unmounted.
         * @returns {void}
         */
        setRouteMapRef(uid, routeMap) {
            if (routeMap) {
                this.routeMapRefs[uid] = routeMap;
                this.routeMapExpandedByUid[uid] = !!routeMap.expanded;
            } else {
                delete this.routeMapRefs[uid];
                delete this.routeMapExpandedByUid[uid];
            }
        },
        /**
         * Toggle the map for a specific entry and persist its expanded state.
         *
         * @param {string} uid - Entry unique identifier.
         * @returns {Promise<void>}
         */
        async toggleRouteMap(uid) {
            const routeMapRef = this.routeMapRefs[uid];
            if (routeMapRef?.toggleMap) {
                const isExpanded = await routeMapRef.toggleMap();
                this.routeMapExpandedByUid[uid] = isExpanded;
            }
        },
        /**
         * Check whether the route map is currently expanded for an entry.
         *
         * @param {string} uid - Entry unique identifier.
         * @returns {boolean} True when the map is expanded.
         */
        isRouteMapExpanded(uid) {
            return !!this.routeMapExpandedByUid[uid];
        },
        /**
         * Copy an entry ID to clipboard.
         *
         * @param {string} entryId - Entry identifier to copy.
         * @returns {Promise<void>}
         */
        async copyEntryId(entryId) {
            if (!entryId) return;

            try {
                await navigator.clipboard.writeText(entryId);
                this.showAlertModal(
                    this.$store,
                    this.$t("entries.entryIdCopied", { id: entryId }),
                    "success",
                    1500
                );
            } catch (error) {
                console.error("Failed to copy entry ID:", error);
            }
        },
        async fetchViewers(entry) {
            if (this.viewersCache[entry.uid]) return;
            const isTruck = this.entries[0]?.form_type === "truck";
            const url = isTruck
                ? `${process.env.VUE_APP_API_URL}/truck-requests/${entry.uid}/viewers/`
                : `${process.env.VUE_APP_API_URL}/ware-requests/${entry.uid}/viewers/`;
            try {
                const { data } = await axios.get(url);
                this.viewersCache[entry.uid] = data;
            } catch (e) {
                this.viewersCache[entry.uid] = [];
            }
        },
        getPaymentIncidentsCount(entry) {
            return entry?.company?.payment_incidents_count ?? 0;
        },
        getTransportIncidentsCount(entry) {
            return entry?.company?.transport_incidents_count ?? 0;
        },
        getTotalIncidents(entry) {
            return (
                this.getPaymentIncidentsCount(entry) +
                this.getTransportIncidentsCount(entry)
            );
        },
        goToCompanyIncidents(companyId) {
            if (!companyId) return;
            this.$router.push({
                name: "OtherCompanyIncidents",
                query: { companyId },
            });
        },
        goToCompanyDetails(companyId) {
            if (!companyId) return;
            this.$router.push({
                name: "CompanyDetails",
                params: { id: companyId },
            });
        },
        /**
         * Toggle the expanded state of a card and, if opening,
         * record a view on the server and update the local view count.
         *
         * @param {string} uid - The unique identifier of the entry to expand.
         * @returns {Promise<void>}
         */
        async showCardDetails(uid) {
            const wasExpanded = this.expandedEntryUid === uid;

            if (wasExpanded) {
                this.setActiveCalculatorPanel(uid, null);
            } else if (this.expandedEntryUid) {
                this.setActiveCalculatorPanel(this.expandedEntryUid, null);
            }

            const opening = !wasExpanded;
            this.expandedEntryUid = opening ? uid : null;

            if (opening) {
                const isTruck = this.entries[0]?.form_type === "truck";
                const endpoint = isTruck
                    ? `${process.env.VUE_APP_API_URL}/truck-requests/${uid}/view/`
                    : `${process.env.VUE_APP_API_URL}/ware-requests/${uid}/view/`;
                try {
                    const { data } = await axios.post(endpoint);
                    const idx = this.entries.findIndex((e) => e.uid === uid);
                    if (idx !== -1) {
                        this.entries[idx].view_count = data.view_count;
                        this.fetchViewers(this.entries[idx]);
                    }
                } catch (e) {
                    console.error("Failed to record view:", e);
                }
            }
        },
        async refreshEntry(uid) {
            try {
                const isTruck = this.entries[0]?.form_type === "truck";
                const endpoint = isTruck
                    ? `${process.env.VUE_APP_API_URL}/truck-requests/${uid}/refresh/`
                    : `${process.env.VUE_APP_API_URL}/ware-requests/${uid}/refresh/`;

                const response = await axios.post(endpoint);
                const updatedEntry = response.data;

                // Replace local entry (or wait for WebSocket update to handle it)
                const index = this.entries.findIndex((e) => e.uid === uid);
                if (index !== -1) {
                    this.entries.splice(index, 1, {
                        ...this.entries[index],
                        updated_at: updatedEntry.updated_at,
                    });
                }
            } catch (error) {
                console.error("Failed to refresh entry:", error);
                this.showAlertModal(
                    this.$store,
                    "Failed to refresh entry",
                    "danger",
                    12000,
                    error
                );
            }
        },
        ...mapActions(["fetchCountries"]),
        ...mapState(["selectedLanguage"]),
        getReadableValue(enumObj, value, translationKey) {
            if (value === null || value === undefined) {
                return null;
            }

            // find the enum entry (works for both arrays and plain objects)
            const match = Object.values(enumObj).find(
                (item) => item.value === value
            );
            if (!match) return "N/A";

            const valueStr = match.value.toString();

            /* ---------------------------------------------------------
             * 1) try the normal explicit translation, e.g.
             *    transport.truckType["1"]  -> "Sprinter"
             * --------------------------------------------------------- */
            const explicitKey = `transport.${translationKey}.${valueStr}`;
            if (this.$te(explicitKey)) {
                // $te = “translation exists?”
                return this.$t(explicitKey);
            }

            if (match.mu) {
                const unitKey = `transport.units.${match.mu}`;
                const unit = this.$te(unitKey) ? this.$t(unitKey) : match.mu;
                return `${match.text} ${unit}`;
            }

            /* 3) nothing else – give the plain text from enum */
            return match.text;
        },
        async handleWsUpdate(data) {
            const isTruckRoute =
                this.$route.name === "SearchTrucks" ||
                this.$route.name === "MyTrucks";
            const expectedFormType = isTruckRoute ? "truck" : "ware";

            if (data.type === "truck_delete" || data.type === "ware_delete") {
                this.entries = this.entries.filter(
                    (entry) => entry.uid !== data.entry_data.uid
                );
                return;
            }

            if (
                !data.entry_data ||
                data.entry_data.form_type !== expectedFormType
            )
                return;

            const newEntry = data.entry_data;
            const existingIndex = this.entries.findIndex(
                (entry) => entry.uid === newEntry.uid
            );

            if (existingIndex !== -1) {
                this.entries.splice(existingIndex, 1, newEntry);
            } else {
                this.entries.unshift(newEntry);
            }
        },
        // Called when the delete button is clicked
        confirmDelete(uid) {
            if (confirm("Are you sure you want to delete this entry?")) {
                this.deleteEntry(uid);
            }
        },
        async deleteEntry(uid) {
            try {
                const isTruck = this.entries[0].form_type === "truck";
                const endpoint = isTruck
                    ? `${process.env.VUE_APP_API_URL}/truck-requests/${uid}/`
                    : `${process.env.VUE_APP_API_URL}/ware-requests/${uid}/`;

                await axios.delete(endpoint);
                // Remove the deleted entry locally (if not already removed by ws-update)
                this.entries = this.entries.filter(
                    (entry) => entry.uid !== uid
                );
            } catch (error) {
                console.error("Error deleting entry:", error);
                this.showAlertModal(
                    this.$store,
                    "Error deleting entry",
                    "danger",
                    12000,
                    error
                );
            }
        },
        editEntry(uid) {
            const entry = this.entries.find((e) => e.uid === uid);
            if (!entry || entry.user.id !== this.currentUserId) return;
            // Here we use the current route name to decide if it's truck or ware.
            const isTruck =
                this.$route.name === "SearchTrucks" ||
                this.$route.name === "MyTrucks";
            const routeName = isTruck ? "EditTruck" : "EditWare";
            this.$router.push({ name: routeName, params: { uid } });
        },

        async fetchEntries() {
            const routeName = this.$route.name;
            const isTruckRoute =
                routeName === "SearchTrucks" || routeName === "MyTrucks";
            const isUserEntries = this.userEntries;

            const baseEndpoint = isTruckRoute
                ? `${process.env.VUE_APP_API_URL}/truck-requests/`
                : `${process.env.VUE_APP_API_URL}/ware-requests/`;

            const endpoint = isUserEntries
                ? `${baseEndpoint}?my=true`
                : baseEndpoint;

            try {
                const response = await axios.get(endpoint);
                const entriesData = response.data;
                if (!entriesData.length) {
                    this.entries = [];
                    this.showAlertModal(
                        this.$store,
                        this.$t("entries.noEntries"),
                        "warning",
                        5000
                    );
                    return;
                }

                this.entries = entriesData;
                if (this.highlightUid) {
                    const idx = this.entries.findIndex(
                        (e) => e.uid === this.highlightUid
                    );
                    if (idx !== -1) {
                        const [hit] = this.entries.splice(idx, 1);
                        this.entries.unshift(hit);
                        setTimeout(() => {
                            this.expandedEntryUid = this.highlightUid;
                        }, 500);
                    }
                }
            } catch (error) {
                console.error("Error fetching entries:", error);
                this.showAlertModal(
                    this.$store,
                    "Error fetching entries",
                    "danger",
                    12000,
                    error
                );
                this.entries = [];
            }
        },

        formatLocalTime,
        formatDateRange,
        canShowReserveButton(entry) {
            if (!this.currentUserId || !this.currentUserCompanyId) return false;
            if (entry.company?.id === this.currentUserCompanyId) return false;
            if (entry.form_type === "ware") {
                if (entry.has_pending_reservation || entry.has_accepted_reservation) {
                    return false;
                }
            }
            if (entry.form_type === "truck" && entry.is_full) return false;
            return true;
        },
        canShowMarkTruckFullOrAvailableButton(entry) {
            if (!this.currentUserId || !this.currentUserCompanyId) return false;
            if (entry.form_type !== "truck") return false;
            if (entry.company?.id !== this.currentUserCompanyId) return false;
            return true;
        },
        isExpressTruck(entry) {
            const transportType =
                entry.transport_options?.truck_transport_type_value ??
                entry.transport_options?.truck_transport_type;
            return Number(transportType) === TRUCK_TRANSPORT_TYPE.EXPRES.value;
        },
        isEntryGreyedOut(entry) {
            if (entry.has_pending_reservation) return true;
            if (entry.form_type === "ware" && entry.has_accepted_reservation) return true;
            if (entry.form_type === "truck" && entry.is_full) return true;
            if (
                entry.form_type === "truck" &&
                this.isExpressTruck(entry) &&
                entry.has_active_reservation
            ) {
                return true;
            }
            return false;
        },
        entryReservedTooltip(entry) {
            if (entry.has_pending_reservation) {
                return this.$t("entries.entryInNegotiation");
            }
            if (entry.form_type === "truck" && entry.is_full) {
                return `${this.$t("entries.entryReserved")} ${this.$t(
                    "entries.truckMarkedFullByOwner"
                )}`;
            }
            return this.$t("entries.entryReserved");
        },
        handleEntryClick(entry) {
            // Prefer closing an open calculator over collapsing the big card.
            if (this.getActiveCalculatorPanel(entry.uid)) {
                this.setActiveCalculatorPanel(entry.uid, null);
                return;
            }
            if (this.wasCalculatorJustClosed(entry.uid)) {
                return;
            }
            // Greyed cards still expand/collapse; contents stay non-interactive via CSS.
            this.showCardDetails(entry.uid);
        },
        openReservationModal(entry) {
            this.reservationEntryForModal = entry;
            this.reservationModalVisible = true;
        },
        openMarkTruckFullConfirm(entry) {
            this.entryToMarkFull = entry;
        },
        confirmMarkTruckFull() {
            if (!this.entryToMarkFull) return;
            this.markTruckFull(this.entryToMarkFull);
            this.entryToMarkFull = null;
        },
        openMarkTruckAvailableConfirm(entry) {
            this.entryToMarkAvailable = entry;
        },
        confirmMarkTruckAvailable() {
            if (!this.entryToMarkAvailable) return;
            this.markTruckAvailable(this.entryToMarkAvailable);
            this.entryToMarkAvailable = null;
        },
        async markTruckFull(entry) {
            try {
                const { data } = await axios.post(
                    `${process.env.VUE_APP_API_URL}/truck-requests/${entry.uid}/mark-full/`
                );
                const idx = this.entries.findIndex((e) => e.uid === entry.uid);
                if (idx !== -1) {
                    this.entries[idx] = { ...this.entries[idx], ...data };
                }
                if (this.expandedEntryUid === entry.uid) {
                    this.expandedEntryUid = null;
                }
            } catch (err) {
                console.error("Failed to mark truck full:", err);
                this.showAlertModal(this.$store, err.response?.data?.error || "Failed to mark truck as full", "danger", 5000);
            }
        },
        async markTruckAvailable(entry) {
            try {
                const { data } = await axios.post(
                    `${process.env.VUE_APP_API_URL}/truck-requests/${entry.uid}/mark-available/`
                );
                const idx = this.entries.findIndex((e) => e.uid === entry.uid);
                if (idx !== -1) {
                    this.entries[idx] = { ...this.entries[idx], ...data };
                }
                if (this.expandedEntryUid === entry.uid) {
                    this.expandedEntryUid = null;
                }
            } catch (err) {
                console.error("Failed to mark truck available:", err);
                this.showAlertModal(this.$store, err.response?.data?.error || "Failed to mark truck as available", "danger", 5000);
            }
        },
        async submitReservation(payload) {
            if (!this.reservationEntryForModal) return;
            const entry = this.reservationEntryForModal;
            const reservedEntryUid = entry.uid;
            this.$refs.reservationFormRef?.setSubmitting?.(true);
            try {
                await axios.post(`${process.env.VUE_APP_API_URL}/reservations/`, {
                    entry_uid: entry.uid,
                    entry_type: entry.form_type,
                    ...payload,
                });
                this.reservationModalVisible = false;
                this.reservationEntryForModal = null;
                await this.fetchEntries();
                if (reservedEntryUid && this.expandedEntryUid === reservedEntryUid) {
                    this.expandedEntryUid = null;
                }
                const hasPriceOffer = payload && payload.price_offer != null;
                const successMessageKey = hasPriceOffer
                    ? "reservation.createdSuccessWithOffer"
                    : "reservation.createdSuccess";
                this.showAlertModal(this.$store, this.$t(successMessageKey), "success", 3000);
            } catch (err) {
                const errorCode = err.response?.data?.error;
                const localeKey = getReservationCreateErrorKey(errorCode);
                const msg = localeKey ? this.$t(localeKey) : (errorCode || this.$t("reservation.createFailed"));
                this.showAlertModal(this.$store, msg, "danger", 5000);
            } finally {
                this.$refs.reservationFormRef?.setSubmitting?.(false);
            }
        },
    },
    computed: {
        reserveDialogCardStyle() {
            if (!this.reserveDialogMaxHeight) return {};
            return { maxHeight: this.reserveDialogMaxHeight };
        },
        ...mapState(["countries"]),
        currentUserId() {
            const user = JSON.parse(sessionStorage.getItem("user") || "{}");
            return user?.id || null;
        },
        currentUserCompanyId() {
            const user = JSON.parse(sessionStorage.getItem("user") || "{}");
            return user?.company || null;
        },
        listFormType() {
            const p = this.$route.path || "";
            if (p.includes("/my-wares")) return "ware";
            if (p.includes("/my-trucks")) return "truck";
            if (this.entries.length) return this.entries[0].form_type;
            return "truck";
        },
        filteredAndSortedEntries() {
            // 1) entries already translated & sorted
            const allEntries = this.sortedEntries;

            // 2) pull current filters
            const {
                loadingLocation,
                unloadingLocation,
                weight: maxWeight,
                mdp: maxMdp,
                expiresIn,
                identificationId,
                showReservedWareOnly,
            } = this.filters;

            const loadingCountryCode = loadingLocation.country_code;
            const loadingPlaceId = loadingLocation.place_id;
            const unloadingCountryCode = unloadingLocation.country_code;
            const unloadingPlaceId = unloadingLocation.place_id;

            return allEntries.filter((entry) => {
                if (this.listFormType === "ware") {
                    if (showReservedWareOnly) {
                        if (!entry.has_accepted_reservation) return false;
                    } else if (entry.has_accepted_reservation) {
                        return false;
                    }
                }

                // Taken trucks leave the open list (like accepted ware):
                // - Express after accept (not while pending/negotiation)
                // - Any truck marked full by the owner
                if (this.listFormType === "truck") {
                    const isAcceptedExpress =
                        this.isExpressTruck(entry) &&
                        entry.has_active_reservation &&
                        !entry.has_pending_reservation;
                    const isTakenTruck = isAcceptedExpress || entry.is_full;
                    if (showReservedWareOnly) {
                        if (!isTakenTruck) return false;
                    } else if (isTakenTruck) {
                        return false;
                    }
                }

                if (identificationId) {
                    const needle = identificationId.trim().toUpperCase();
                    const hay = (entry.identification_id || "").toUpperCase();
                    if (!hay.includes(needle)) return false;
                }

                if (
                    (loadingCountryCode || loadingPlaceId) &&
                    !this.stopMatches(this.loadingStops(entry), loadingCountryCode, loadingPlaceId)
                ) {
                    return false;
                }
                if (
                    (unloadingCountryCode || unloadingPlaceId) &&
                    !this.stopMatches(this.unloadingStops(entry), unloadingCountryCode, unloadingPlaceId)
                ) {
                    return false;
                }
                /* ---------- numeric caps ---------- */
                if (maxWeight !== null && entry.info.weight > maxWeight)
                    return false;
                if (maxMdp !== null && entry.info.mdp > maxMdp) return false;
                /* ---------- expires in ---------- */
                if (expiresIn) {
                    const hours = expiresIn === "4h" ? 4 : expiresIn === "6h" ? 6 : 24;
                    if (!entryExpiresWithin(entry, hours)) return false;
                }

                return true;
            });
        },
        translatedEntries() {
            return this.entries.map((entry) => ({
                ...entry,
                modified_at: entry.modified_at,
                loading_location: entry.loading_location,
                unloading_location: entry.unloading_location,
                transport_options: {
                    ...entry.transport_options,
                    truck_transport_type_value:
                        entry.transport_options?.truck_transport_type,
                    truck_transport_type: this.getReadableValue(
                        TRUCK_TRANSPORT_TYPE,
                        entry.transport_options?.truck_transport_type,
                        "truckTransportType"
                    ),
                    truck_type: this.getReadableValue(
                        TRUCK_TYPE,
                        entry.transport_options?.truck_type,
                        "truckType"
                    ),
                    truck_coating: this.getReadableValue(
                        TRUCK_COATING,
                        entry.transport_options?.truck_coating,
                        "truckCoating"
                    ),
                    truck_special_features: Array.isArray(
                        entry.transport_options?.truck_special_features
                    )
                        ? entry.transport_options.truck_special_features
                              .map((val) =>
                                  this.getReadableValue(
                                      TRUCK_SPECIAL_FEATURE,
                                      val,
                                      "truckSpecialFeature"
                                  )
                              )
                              .join(", ")
                        : this.getReadableValue(
                              TRUCK_SPECIAL_FEATURE,
                              entry.transport_options?.truck_special_feature,
                              "truckSpecialFeature"
                          ),
                    platform_type: Array.isArray(
                        entry.transport_options?.platform_type
                    )
                        ? entry.transport_options.platform_type
                              .map((val) =>
                                  this.getReadableValue(
                                      PLATFORM_TYPE,
                                      val,
                                      "platformType"
                                  )
                              )
                              .join(", ")
                        : this.getReadableValue(
                              PLATFORM_TYPE,
                              entry.transport_options?.platform_type,
                              "platformType"
                          ),
                    dangerous_load: entry.transport_options?.dangerous_load,
                    escrow_account: entry.transport_options?.escrow_account,
                },
                info: {
                    ...entry.info,
                    payment_term: this.getReadableValue(
                        PAYMENT_DEADLINE,
                        entry.info?.payment_term,
                        "paymentTerms"
                    ),
                },
            }));
        },
        sortedEntries() {
            const sorted = this.translatedEntries.slice().sort((a, b) => {
                const dateA = new Date(a.modified_at);
                const dateB = new Date(b.modified_at);
                return this.sortDirection === "desc"
                    ? dateB - dateA // newest → oldest
                    : dateA - dateB; // oldest → newest
            });
            return sorted;
        },
        paginatedEntries() {
            const start = (this.page - 1) * this.pageSize;
            return this.filteredAndSortedEntries.slice(start, start + this.pageSize);
        },
    },
    watch: {
        reservationModalVisible(visible) {
            if (visible) this.measureReserveDialog();
            else this.reserveDialogMaxHeight = null;
        },
        $route(to, from) {
            this.page = 1;
            this.applyRouteQueryFilters();
            this.fetchEntries();
        },
        filters: {
            deep: true,
            handler() {
                this.page = 1;
            },
        },
        sortDirection() {
            this.page = 1;
        },
        pageSize(nextPageSize) {
            savePageSize(nextPageSize);
            this.page = 1;
        },
        filteredAndSortedEntries(list) {
            const maxPage = Math.max(1, Math.ceil(list.length / this.pageSize) || 1);
            if (this.page > maxPage) {
                this.page = maxPage;
            }
            if (!this.expandedEntryUid) return;
            const expanded = (list || []).find(
                (entry) => entry.uid === this.expandedEntryUid
            );
            // Collapse only if the entry left the filtered list (e.g. accepted ware).
            if (!expanded) {
                this.expandedEntryUid = null;
            }
        },
    },
};
</script>

<style scoped>
.entries-list {
    position: relative;
    z-index: 2;
}

.entry-card-toolbar :deep(.v-toolbar__content) {
    height: auto !important;
    min-height: var(--v-toolbar-height);
    padding-top: 12px;
    padding-bottom: 12px;
    overflow: visible;
}

.entry-card-toolbar-title :deep(.v-toolbar-title__placeholder) {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
    row-gap: 8px;
}

.entry-card-toolbar--sm :deep(.v-toolbar__content) {
    height: auto !important;
    align-items: stretch;
}

.entry-card-toolbar--sm :deep(.v-toolbar__extension) {
    height: auto !important;
}

.company-router-link {
    text-decoration: none;
    color: inherit;
    display: block;
    padding: 12px;
    border-radius: 8px;
    transition: background-color 0.2s ease;
}

.company-router-link:hover {
    background-color: #f2f2f2;
    cursor: pointer;
}


.entry-icon-small {
    width: 60px;
    height: 18px;
    vertical-align: top;
}

.entry-icon-big {
    width: 60px;
    height: 18px;
    vertical-align: middle;
}

.entry-stop-group {
    display: grid;
    grid-template-columns: auto auto minmax(0, 1fr);
    column-gap: 6px;
    row-gap: 4px;
    align-items: start;
    font-weight: 700;
}

.reserve-dialog-body {
    min-height: 0;
}

.heading-icon-truck {
    width: 50px;
    height: 32px;
    vertical-align: middle;
}

.heading-icon-ware {
    width: 30px;
    height: 32px;
    vertical-align: middle;
}

.big-card {
    cursor: default;
}

@media (max-width: 700px) {
    .entry-small-card__layout {
        flex-direction: column;
    }

    .entry-small-card__estimates {
        order: -1;
        width: 100%;
        flex-direction: row !important;
        flex-wrap: wrap;
        justify-content: center;
        gap: 8px 16px;
        padding-top: 8px !important;
        padding-bottom: 8px !important;
        white-space: normal;
    }
}

.entry-small-card__estimate-row + .entry-small-card__estimate-row {
    margin-top: 6px;
}

@media (max-width: 700px) {
    .entry-small-card__estimate-row + .entry-small-card__estimate-row {
        margin-top: 0;
    }
}

.toolbar-item-separator {
    display: inline-block;
    height: 32px;
    margin: 0 8px;
    border-left: 2px solid #FFEB3B;
    vertical-align: middle;
}

.entry-toolbar-reserve-btn {
    min-width: min(280px, calc(100% - 24px));
    font-weight: 700;
    letter-spacing: 0.02em;
}

/* Vuetify has no breakpoint below xs (<600px); use custom width when layout breaks narrower */
@media (max-width: 473px) {
    .toolbar-item-separator--invisible-max-473 {
        visibility: hidden;
    }
}

.toolbar-left-group {
    display: inline-flex;
    align-items: center; 
    vertical-align: middle;
}

.distance-toggle:hover {
    opacity: 0.9;
}

.google-maps-icon {
    width: 24px;
    height: 24px;
}

/* Match v-tooltip content styling exactly (same as other tooltips on the card) */
.viewers-menu-list {
    background: rgb(var(--v-theme-surface-variant));
    color: rgb(var(--v-theme-on-surface-variant));
    border-radius: 4px;
    font-size: 0.875rem;
    line-height: 1.6;
    padding: 5px 0;
    min-width: 200px;
    max-height: 280px;
    overflow-y: auto;
}

.viewer-item {
    cursor: pointer;
}
.viewer-item:hover {
    background-color: rgba(var(--v-theme-on-surface-variant), 0.12);
}

.entry-greyed-out {
    opacity: 0.65;
    cursor: pointer;
}
.entry-greyed-out * {
    pointer-events: none;
}
.entry-greyed-out .mark-available-btn,
.entry-greyed-out .mark-available-btn * {
    pointer-events: auto;
}

</style>
